import React from 'react';
import PropTypes from 'prop-types';
import { a11yProps, focusHandlerProps, getTokensPropType, inputSupportsProps, selectSystemProps, textInputHandlerProps, textInputProps, variantProp, viewProps } from '../utils';
import InputSupports from '../InputSupports';
import TextInputBase from './TextInputBase';
import textInputPropTypes from './propTypes';
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
const [selectProps, selectedSystemPropTypes] = selectSystemProps([a11yProps, focusHandlerProps, inputSupportsProps, textInputHandlerProps, textInputProps, viewProps]);

/**
 * A basic text input component. Use in forms or individually to receive user's input.
 * Due to React Native's implementation of `TextInput` it's not possible to access the current value by passing a ref.
 *
 * ## Controlled component
 * If it is required that the state of the `TextInput` be controlled by the application or other external methods,
 * `value` and `onChange` props must be passed to the `TextInput`.
 * If the `TextInput` value should not be changed by user input, a `readOnly` prop must be provided.
 *
 * ## Uncontrolled component
 * If it is not necessary to control the `TextInput` state, you can create one without a value prop.
 * In this case its value can be accessed within the `onChange` callback.
 *
 * NOTE: In order to support more advanced usages all additional props will be passed down to the React Native's
 * `TextInput` component. See <a href="https://reactnative.dev/docs/textinput" target="_blank">React Native documentation</a> for
 * supported props and <a href="https://reactnative.dev/docs/textinput" target="_blank">React Native Web documentation</a> for
 * their implementation on the web.
 */
const TextInput = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  let {
    tokens,
    nativeID,
    variant = {},
    ...rest
  } = _ref;
  const {
    supportsProps,
    ...selectedProps
  } = selectProps(rest);
  const inputProps = {
    ...selectedProps,
    tokens
  };
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(InputSupports, {
      nativeID: nativeID,
      ...supportsProps,
      inputValue: selectedProps?.value,
      children: _ref2 => {
        let {
          inputId,
          ...propsFromInputSupports
        } = _ref2;
        return /*#__PURE__*/_jsx(TextInputBase, {
          ref: ref,
          nativeID: inputId,
          ...propsFromInputSupports,
          ...inputProps,
          variant: {
            ...variant,
            validation: propsFromInputSupports.validation
          }
        });
      }
    }), rest.children]
  });
});
TextInput.displayName = 'TextInput';
TextInput.propTypes = {
  ...selectedSystemPropTypes,
  ...textInputPropTypes,
  /**
   * A callback which if provided will get a clear button rendered and will be called whenever that button gets pressed.
   */
  onClear: PropTypes.func,
  onKeyPress: PropTypes.func,
  tokens: getTokensPropType('TextInput'),
  variant: variantProp.propType
};
export default TextInput;